import { URL_CONSTANTS } from "../../Everou/_constants/url.constants";

// One-time cross-domain session handoff for the *.baintex.com -> *.ai360access.com
// migration.
//
// The login is not a cookie: it is the user.apikey held in the redux state and
// persisted to localStorage under the "state" key (see LocalStorageHelper /
// PersistedStateLoader). localStorage is scoped per origin and baintex.com /
// ai360access.com are different registrable domains, so the session cannot be
// shared by cookies either. To avoid logging everyone out on the day the domain
// changes, the session is carried across via a short-lived, single-use handoff
// token (a nonce), NOT the apikey itself:
//
//   - On a legacy *.baintex.com host the app mints a token from the backend
//     (POST /user/login/handoff, authenticated with the current apikey) and
//     redirects to the equivalent *.ai360access.com URL carrying that token plus
//     the device id in the fragment (#h=<token>&m=<machine_uid>).
//   - On the new host the app redeems the token (POST /user/login/handoff/redeem,
//     public) for the apikey, writes it (and the carried machine_uid) into its own
//     localStorage and strips the fragment before rendering.
//
// What travels in the URL is a random 60s single-use token plus the machine_uid
// (a plain device UUID): neither is a credential, and neither looks like the
// obfuscated payload that tripped Chrome's anti-phishing heuristic when the
// apikey/state travelled in the URL. The device id must be carried because the
// websocket broker authenticates the (machine_uid, apikey) pair, so the new host
// has to reuse the same machine_uid the apikey is already registered under
// instead of minting a fresh one. Both domains share the same backend, so the
// apikey stays valid on the new host.
//
// This only preserves the login while baintex.com is still served by this SPA
// (a client-side redirect). A plain 3xx redirect at the DNS/proxy layer runs
// before any JS, so it cannot mint/carry the token and the user would be logged
// out. Keep serving the SPA on baintex.com during the transition.

const LEGACY_DOMAIN = "baintex.com";
const TARGET_DOMAIN = "ai360access.com";

// TEMP: set to false to disable the *.baintex.com -> *.ai360access.com redirect
// (for testing). Set back to true to re-enable the migration. The outbound tests
// are gated on this flag, so they re-activate when it flips back to true.
export const REDIRECT_TO_TARGET_ENABLED = true;

const HANDOFF_HASH_KEY = "h";
const MACHINE_UID_HASH_KEY = "m";
const STATE_STORAGE_KEY = "state";
const MACHINE_UID_STORAGE_KEY = "machine_uid";

// Maps a legacy hostname to its *.ai360access.com equivalent, preserving the
// subdomain. Returns null when the hostname is not a legacy host.
export function mapLegacyHostname(hostname) {
    if (hostname === LEGACY_DOMAIN) {
        return TARGET_DOMAIN;
    }

    if (hostname.endsWith(`.${LEGACY_DOMAIN}`)) {
        const subdomain = hostname.slice(0, -(LEGACY_DOMAIN.length + 1));
        return `${subdomain}.${TARGET_DOMAIN}`;
    }

    return null;
}

// Runs both sides of the handoff. Returns a promise resolving to true when an
// outbound redirect was triggered, so the caller can skip rendering the app.
export async function runDomainMigration({
    location = window.location,
    storage = safeStorage(),
    history = window.history,
    fetchFn = defaultFetch(),
    apiUrl = URL_CONSTANTS.URL_API,
    appApiKey = URL_CONSTANTS.API_KEY,
} = {}) {
    await consumeHandoff({ location, storage, history, fetchFn, apiUrl, appApiKey });
    return redirectLegacyHostToTarget({ location, storage, fetchFn, apiUrl });
}

async function redirectLegacyHostToTarget({ location, storage, fetchFn, apiUrl }) {
    if (!REDIRECT_TO_TARGET_ENABLED) {
        return false;
    }

    const targetHostname = mapLegacyHostname(location.hostname);
    if (!targetHostname) {
        return false;
    }

    const host = location.port
        ? `${targetHostname}:${location.port}`
        : targetHostname;

    let hash = "";
    const apikey = getApikeyFromStorage(storage);
    if (apikey) {
        const token = await mintHandoffToken({ apikey, fetchFn, apiUrl });
        if (token) {
            hash = `#${HANDOFF_HASH_KEY}=${encodeURIComponent(token)}`;

            // Carry the device id so the new host keeps the same machine_uid the
            // apikey is registered under (the websocket broker authenticates the
            // uid+apikey pair; a freshly generated uid on the new origin is
            // rejected as an invalid apikey).
            const machineUid = getMachineUid(storage);
            if (machineUid) {
                hash += `&${MACHINE_UID_HASH_KEY}=${encodeURIComponent(machineUid)}`;
            }
        }
    }

    location.replace(
        `${location.protocol}//${host}${location.pathname}${location.search}${hash}`,
    );

    return true;
}

async function consumeHandoff({ location, storage, history, fetchFn, apiUrl, appApiKey }) {
    const token = readHashParam(location.hash, HANDOFF_HASH_KEY);
    if (!token) {
        return;
    }

    try {
        // Never clobber a session that already exists on the target domain.
        if (storage && !getApikeyFromStorage(storage)) {
            const apikey = await redeemHandoffToken({ token, fetchFn, apiUrl, appApiKey });
            if (apikey) {
                storage.setItem(STATE_STORAGE_KEY, buildSessionState(apikey));

                // Reuse the machine_uid the apikey is registered under instead of
                // letting MachineUidHelper mint a fresh one on this origin, so the
                // websocket broker accepts the uid+apikey pair.
                const machineUid = readHashParam(location.hash, MACHINE_UID_HASH_KEY);
                if (machineUid) {
                    storage.setItem(MACHINE_UID_STORAGE_KEY, machineUid);
                }
            }
        }
    } catch (e) {
        // Failed handoff: ignore it and let the user log in normally.
    } finally {
        stripHandoffFromHash(location, history);
    }
}

async function mintHandoffToken({ apikey, fetchFn, apiUrl }) {
    try {
        const response = await fetchFn(`${apiUrl}${URL_CONSTANTS.USER_LOGIN_HANDOFF}`, {
            method: "POST",
            headers: {
                Authorization: "Basic " + apikey,
                "Content-Type": "application/json",
            },
            body: "{}",
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return (data && data.token) || null;
    } catch (e) {
        return null;
    }
}

async function redeemHandoffToken({ token, fetchFn, apiUrl, appApiKey }) {
    try {
        const response = await fetchFn(`${apiUrl}${URL_CONSTANTS.USER_LOGIN_HANDOFF_REDEEM}`, {
            method: "POST",
            headers: {
                Authorization: appApiKey,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ token }),
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return (data && data.apikey) || null;
    } catch (e) {
        return null;
    }
}

// Minimal persisted state: just enough for the boot-time refreshUser() to see a
// logged-in user and refetch the full profile from the backend with this apikey.
function buildSessionState(apikey) {
    return JSON.stringify({
        common: { authentication: { user: { apikey } } },
    });
}

function getMachineUid(storage) {
    if (!storage) {
        return null;
    }

    try {
        return storage.getItem(MACHINE_UID_STORAGE_KEY) || null;
    } catch (e) {
        return null;
    }
}

function getApikeyFromStorage(storage) {
    if (!storage) {
        return null;
    }

    try {
        const parsed = JSON.parse(storage.getItem(STATE_STORAGE_KEY));
        return (
            parsed &&
            parsed.common &&
            parsed.common.authentication &&
            parsed.common.authentication.user &&
            parsed.common.authentication.user.apikey
        );
    } catch (e) {
        return null;
    }
}

function readHashParam(hash, key) {
    if (!hash) {
        return null;
    }

    return new URLSearchParams(hash.replace(/^#/, "")).get(key);
}

function stripHandoffFromHash(location, history) {
    const params = new URLSearchParams((location.hash || "").replace(/^#/, ""));
    params.delete(HANDOFF_HASH_KEY);
    params.delete(MACHINE_UID_HASH_KEY);

    const remaining = params.toString();
    const url = `${location.pathname}${location.search}${remaining ? `#${remaining}` : ""}`;

    if (history && history.replaceState) {
        history.replaceState(null, "", url);
    } else {
        location.hash = remaining;
    }
}

function defaultFetch() {
    return typeof window !== "undefined" && window.fetch
        ? window.fetch.bind(window)
        : undefined;
}

function safeStorage() {
    try {
        return window.localStorage;
    } catch (e) {
        return null;
    }
}
